// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompareChanges.test.tsx
import { type AskPunttComparisonRegion } from "@mg/schemas/src/christo/versionComparison";
import { useTranslation } from "react-i18next";

const SUMMARY_KEYS = {
  text: "askV2.review.compareSummaryWording",
  pixels: "askV2.review.compareSummaryPicture",
  moved: "askV2.review.compareSummaryMoved",
} as const;

/**
 * The box under the changes list: how many changes of each kind, and how
 * many fix a comment.
 */
export function AskV2ReviewCompareSummary({
  regions,
  fixingCount = 0,
}: {
  regions: readonly AskPunttComparisonRegion[];
  /** How many of the changes fix a comment the older version raised. */
  fixingCount?: number;
}) {
  const { t } = useTranslation("ui");
  const counts = { text: 0, pixels: 0, moved: 0 };
  for (const region of regions) counts[region.evidence] += 1;
  const lines = (Object.keys(SUMMARY_KEYS) as (keyof typeof counts)[]).filter(
    (kind) => counts[kind] > 0,
  );
  if (lines.length === 0) return null;
  return (
    <section
      aria-label={t("askV2.review.compareSummaryLabel")}
      className="mx-4 mb-3 flex shrink-0 flex-col gap-1 rounded-lg bg-puntt-neutral-gray-2 p-3 text-[12px] text-puntt-neutral-gray-11"
      data-testid="ask-v2-review-compare-summary"
    >
      <span className="font-semibold text-puntt-neutral-gray-12">
        {t("askV2.review.compareSummaryLabel")}
      </span>
      {lines.map((kind) => (
        <span key={kind} data-testid={`ask-v2-review-compare-summary-${kind}`}>
          {t(SUMMARY_KEYS[kind], { count: counts[kind] })}
        </span>
      ))}
      {fixingCount > 0 ? (
        <span data-testid="ask-v2-review-compare-summary-fixes">
          {t("askV2.review.compareSummaryFixes", { count: fixingCount })}
        </span>
      ) : null}
    </section>
  );
}
